import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import "./MentionTextarea.css";

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
// Text typed after "@" up to the caret, e.g. "Hello @dik" -> "dik". Allows spaces so full names can be typed.
const MENTION_AT_CARET = /(?:^|\s)@([^@\n]{0,40})$/;

// "@all" tags everyone, like WhatsApp. It's a separate entry, not a person.
const ALL_ID = "__all__";
const ALL_TAG = "all";
const ALL_IN_TEXT = /(?:^|\s)@all(?![\w])/i;
// True when `text` tags "@all" (e.g. to send every person's id with the message).
export const mentionsAll = (text) => ALL_IN_TEXT.test(text || "");

// Regex matching "@Full Name" for any of the given people (longest names first),
// plus "@all" when `withAll` is set.
const mentionRegex = (people, withAll = false) => {
  const names = (people || []).map((p) => p.name).filter(Boolean).sort((a, b) => b.length - a.length).map(escapeRegex);
  if (withAll) names.push(`${ALL_TAG}(?![\\w])`);
  return names.length ? new RegExp(`@(?:${names.join("|")})`, "g") : null;
};

// Splits text into plain strings and <mark>ed "@Name" tags.
const highlightMentions = (text, pattern) => {
  if (!text || !pattern) return text;
  const parts = [];
  let last = 0;
  text.replace(pattern, (m, offset) => {
    parts.push(text.slice(last, offset), <mark key={offset} className="ds-mention__tag">{m}</mark>);
    last = offset + m.length;
    return m;
  });
  parts.push(text.slice(last));
  return parts;
};

// Read-only text with "@Name" tags highlighted (e.g. a sent chat message);
// `withAll` also highlights "@all".
export function MentionText({ text, people, withAll = false }) {
  const pattern = useMemo(() => mentionRegex(people, withAll), [people, withAll]);
  return <>{highlightMentions(text, pattern)}</>;
}

// WhatsApp-style tagging: type "@" to pick a person; tagged names are highlighted in the text.
//  - people: [{ id, name, subtitle? }] (subtitle, e.g. a role, is shown in the list)
//  - singleLine: chat-style box; Enter calls onSubmit (or picks a name while the list is open)
//  - menuPlacement: "bottom" (default) or "top" for boxes at the bottom of a panel
//  - onPick(person): called when a name is picked (lets callers keep the tagged person's id)
//  - allOption: also offer "@all" (everyone in `people`) at the top of the list, like WhatsApp.
//    Picking it inserts "@all" and calls onPick for every person.
//  - allNoun: what "@all" counts in the list, e.g. "Everyone · 12 students" (default "students").
export default function MentionTextarea({
  value,
  onChange,
  people,
  placeholder,
  singleLine = false,
  onSubmit,
  menuPlacement = "bottom",
  hint = "Type @ to tag a student",
  emptyText = "No matching student",
  noPeopleText = "Select a Balgruha to tag its students",
  showHint = true,
  inputId,
  ariaLabel,
  disabled = false,
  onPick,
  allOption = false,
  allNoun = "students",
}) {
  // "@all" is only worth offering when there is more than one person to tag.
  const withAll = Boolean(allOption) && people.length > 1;
  const textareaRef = useRef(null);
  const backdropRef = useRef(null);
  const [mention, setMention] = useState(null); // { start, query }
  const [active, setActive] = useState(0);
  // Caret position to restore once the parent re-renders with the inserted name.
  const pendingCaret = useRef(null);

  // Runs right after the new value is committed and before the next keystroke is handled,
  // so fast typing after picking a name lands after the name (a rAF here was too late).
  useLayoutEffect(() => {
    if (pendingCaret.current == null || !textareaRef.current) return;
    const pos = pendingCaret.current;
    pendingCaret.current = null;
    textareaRef.current.focus();
    textareaRef.current.setSelectionRange(pos, pos);
  }, [value]);

  const matches = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.trim().toLowerCase();
    // Like WhatsApp: names starting with the query first, then a word starting with it, then any match.
    const rank = (name) => {
      const n = name.toLowerCase();
      if (n.startsWith(q)) return 0;
      if (n.split(/\s+/).some((w) => w.startsWith(q))) return 1;
      return n.includes(q) ? 2 : -1;
    };
    const found = people
      .map((p) => ({ p, r: rank(p.name) }))
      .filter((x) => x.r >= 0)
      .sort((a, b) => a.r - b.r)
      .slice(0, 8)
      .map((x) => x.p);
    // "@all" comes first while the query could still be "all" / "everyone".
    const everyone = withAll && (ALL_TAG.startsWith(q) || "everyone".startsWith(q));
    return everyone ? [{ id: ALL_ID, name: ALL_TAG, isAll: true }, ...found] : found;
  }, [mention, people, withAll]);

  const mentionPattern = useMemo(() => mentionRegex(people, withAll), [people, withAll]);

  const detect = (text, caret) => {
    const match = text.slice(0, caret).match(MENTION_AT_CARET);
    if (!match) { setMention(null); return; }
    setMention({ start: caret - match[1].length - 1, query: match[1] });
    setActive(0);
  };

  const pick = (person) => {
    const el = textareaRef.current;
    const caret = el.selectionStart;
    const before = value.slice(0, mention.start);
    const after = value.slice(caret).replace(/^ /, "");
    const inserted = `@${person.name} `;
    pendingCaret.current = before.length + inserted.length;
    onChange(before + inserted + after);
    setMention(null);
    if (onPick) (person.isAll ? people : [person]).forEach(onPick);
  };

  // Once the text after "@" has a space and matches nobody, the user has moved on: hide the list.
  const showMenu = Boolean(mention) && (matches.length > 0 || !/\s/.test(mention.query));

  const onKeyDown = (e) => {
    if (showMenu && matches.length) {
      if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i + 1) % matches.length); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i - 1 + matches.length) % matches.length); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(matches[active]); return; }
    }
    if (showMenu && e.key === "Escape") { e.preventDefault(); setMention(null); return; }
    // Chat box: Enter sends, Shift+Enter is a new line.
    if (singleLine && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (onSubmit) onSubmit();
    }
  };

  const highlighted = useMemo(() => highlightMentions(value, mentionPattern), [value, mentionPattern]);
  const tagged = useMemo(() => people.filter((p) => value.includes(`@${p.name}`)), [people, value]);
  const taggedAll = withAll && ALL_IN_TEXT.test(value);
  const taggedText = taggedAll
    ? ` · Tagged: Everyone (${people.length} ${allNoun})`
    : tagged.length ? ` · Tagged: ${tagged.map((p) => p.name).join(", ")}` : "";

  return (
    <div className={`ds-mention${singleLine ? " ds-mention--single" : ""}`}>
      <div className="ds-mention__box">
        {/* Trailing space keeps the backdrop height in step when the text ends with a newline. */}
        <div className="ds-mention__backdrop" ref={backdropRef} aria-hidden="true">{highlighted} </div>
        <textarea
          ref={textareaRef}
          id={inputId}
          aria-label={ariaLabel}
          rows={singleLine ? 1 : undefined}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }}
          onKeyDown={onKeyDown}
          onClick={(e) => detect(e.target.value, e.target.selectionStart)}
          onBlur={() => setTimeout(() => setMention(null), 150)}
          onScroll={(e) => { if (backdropRef.current) backdropRef.current.scrollTop = e.target.scrollTop; }}
        />
      </div>
      {showMenu && (
        <ul className={`ds-mention__menu${menuPlacement === "top" ? " ds-mention__menu--top" : ""}`} role="listbox">
          {matches.map((p, i) => (
            <li key={p.id} role="option" aria-selected={i === active}>
              <button type="button" className={i === active ? "is-active" : ""} onMouseDown={(e) => { e.preventDefault(); pick(p); }} onMouseEnter={() => setActive(i)}>
                <span className={`ds-mention__avatar${p.isAll ? " ds-mention__avatar--all" : ""}`}>{p.isAll ? "@" : initials(p.name)}</span>
                <span className="ds-mention__who">
                  {p.isAll ? `@${ALL_TAG}` : p.name}
                  {p.isAll ? <small>Everyone · {people.length} {allNoun}</small> : p.subtitle && <small>{p.subtitle}</small>}
                </span>
              </button>
            </li>
          ))}
          {!matches.length && <li className="ds-mention__empty">{people.length ? emptyText : noPeopleText}</li>}
        </ul>
      )}
      {showHint && <small>{hint}{taggedText}</small>}
    </div>
  );
}
